// Modelo da API de Disparos (HIS-APP-notificacoes-007) — espelha os DTOs e
// catálogos do consignado-trabalhador (`src/features/disparos`).
//
// Base: /api-consignado-trabalhador/v1/disparos · role GRANATECH.
//
// Não confundir com `rotina-disparo.model.ts`: aquele é o contrato antigo
// (/rotinas-disparo), ainda usado pela tela NotificacoesConfig. Este é o
// contrato novo, usado pelo redesign (NotificacoesConfigRedesign).
//
// A 007 é só CADASTRO: nada aqui dispara mensagem. Envio real, agendamento
// de job e execução são da 008 — por isso os catálogos devolvem
// `execucaoDisponivel: false`.

// ---------------------------------------------------------------------------
// Enums do contrato (notification.types.ts no backend)
// ---------------------------------------------------------------------------

export enum CanalComunicacao {
  EMAIL = "EMAIL",
  SMS = "SMS",
  WHATSAPP = "WHATSAPP",
}

export enum ProvedorComunicacao {
  SENDGRID = "SENDGRID",
  ZENVIA = "ZENVIA",
  META = "META",
  VONEX = "VONEX",
}

/** CORPO_PROPRIO: texto escrito aqui. REFERENCIA_EXTERNA: template aprovado no provedor. */
export enum ModoTemplate {
  CORPO_PROPRIO = "CORPO_PROPRIO",
  REFERENCIA_EXTERNA = "REFERENCIA_EXTERNA",
}

/** A entidade que o disparo observa. Define filtros, variáveis e origem de contato. */
export enum ContextoDisparo {
  PROPOSTA = "PROPOSTA",
  PROPOSTA_LEILAO = "PROPOSTA_LEILAO",
  COBRANCA = "COBRANCA",
}

export enum TipoGatilho {
  EVENTO = "EVENTO",
  ROTINA = "ROTINA",
  MANUAL = "MANUAL",
}

export enum FrequenciaDisparo {
  MINUTOS = "MINUTOS",
  HORAS = "HORAS",
  DIARIO = "DIARIO",
  SEMANAL = "SEMANAL",
  MENSAL = "MENSAL",
}

export enum TipoPropriedade {
  TEXTO = "TEXTO",
  NUMERO = "NUMERO",
  DATA = "DATA",
  CPF = "CPF",
  CNPJ = "CNPJ",
  EMAIL = "EMAIL",
  TELEFONE = "TELEFONE",
}

export enum FormatoVariavel {
  TEXTO = "TEXTO",
  NUMERO = "NUMERO",
  DECIMAL_BR = "DECIMAL_BR",
  MOEDA_BRL = "MOEDA_BRL",
  DATA_BR = "DATA_BR",
  COMPETENCIA = "COMPETENCIA",
  CPF = "CPF",
  CPF_MASCARADO = "CPF_MASCARADO",
  CNPJ = "CNPJ",
}

/** Os cinco que o backend aceita. Sem DIFERENTE/CONTEM/MAIOR_QUE/MENOR_OU_IGUAL. */
export enum OperadorFiltro {
  IGUAL = "IGUAL",
  EM = "EM",
  MAIOR_OU_IGUAL = "MAIOR_OU_IGUAL",
  MENOR_QUE = "MENOR_QUE",
  ENTRE = "ENTRE",
}

/** Combinador de um grupo de filtros. Publicado pelo catálogo em `combinadores`. */
export enum CombinadorFiltro {
  E = "E",
  OU = "OU",
}

export enum TipoNoFiltro {
  GRUPO = "GRUPO",
  CONDICAO = "CONDICAO",
}

/**
 * Tetos do backend (disparo-filtro.helper.ts). Valem para a árvore inteira.
 *
 * Atenção ao que `MAX_PROFUNDIDADE_FILTRO` significa: a contagem do backend
 * trata **condição como nível**. Uma lista plana já é profundidade 2
 * (raiz → condição) e um subgrupo é 3 (raiz → grupo → condição). Com o teto
 * em 3, cabe **um único nível de aninhamento** — `E( OU( E(...) ) )` daria 4
 * e é recusado.
 */
export const MAX_CONDICOES_FILTRO = 20;
export const MAX_PROFUNDIDADE_FILTRO = 3;

export enum DestinatarioDisparo {
  TOMADOR = "TOMADOR",
  EMPREGADOR = "EMPREGADOR",
}

export type ComponenteExterno = "CORPO" | "BOTAO_URL" | "CABECALHO_IMAGEM";
export type TipoConteudo = "TEXTO" | "HTML";
export type AcaoDisparo = "ENVIAR_EMAIL" | "ENVIAR_SMS" | "ENVIAR_WHATSAPP";

// ---------------------------------------------------------------------------
// Catálogos — GET /disparos/catalogo/*
//
// São seis endpoints, um por aba do cardápio. O protótipo recebe os seis
// juntos em `CatalogoDisparo` (ver fixtures), do mesmo jeito que a tela real
// vai consumir depois de carregá-los em paralelo.
// ---------------------------------------------------------------------------

/** GET /catalogo/eventos — vem da tabela disparo_evento (catálogo vivo). */
export interface EventoCatalogo {
  id: string;
  descricao: string;
  origem: string;
  codigoExterno: number | null;
  contextos: ContextoDisparo[];
  /** Semântica ainda não fechada — o backend só aceita salvar o disparo inativo. */
  definicaoPendente: boolean;
  execucaoDisponivel: boolean;
}

export interface RelacaoCatalogo {
  id: string;
  entidade: string;
  /** Rótulo de negócio da relação ("Tomador", "Solicitação do leilão (histórico)"). */
  descricao?: string;
  cardinalidade: "N:1" | "1:N";
  selecionavel: boolean;
  motivo?: string;
}

/**
 * Propriedade allowlisted de um contexto. O `sql` fica no backend e nunca é
 * exposto — o front só conhece o id (ex.: `tomador.email`).
 *
 * Nem toda propriedade serve pra tudo: `disponivelFiltro` é false quando a
 * propriedade não tem operador declarado (hoje só status e datas filtram),
 * e `contato` marca as que servem de origem de contato do destinatário.
 */
export interface PropriedadeCatalogo {
  id: string;
  descricao: string;
  tipo: TipoPropriedade;
  operadores: OperadorFiltro[];
  valores?: string[];
  contato?: DestinatarioDisparo;
  disponivelTemplate: boolean;
  disponivelFiltro: boolean;
  formatos: FormatoVariavel[];
}

/** GET /catalogo/entidades — um item por contexto. */
export interface EntidadeCatalogo {
  id: ContextoDisparo;
  descricao: string;
  relacoes: RelacaoCatalogo[];
  propriedades: PropriedadeCatalogo[];
}

/** GET /catalogo/acoes */
export interface AcaoCatalogo {
  id: AcaoDisparo;
  canal: CanalComunicacao;
  descricao: string;
  execucaoDisponivel: boolean;
}

export interface CapacidadeCanal {
  canal: CanalComunicacao;
  modos: ModoTemplate[];
  remetentes: string[];
  camposExternos: string[];
  /**
   * Teto do corpo em CORPO_PROPRIO, contado sobre o texto escrito — as chaves
   * de `{{variavel}}` entram na conta, não o valor que elas viram no envio.
   * Ausente quando o canal não publica limite (WhatsApp, que só usa template).
   */
  limiteCaracteres?: number;
}

/** GET /catalogo/canais — o registry de provedores. */
export interface ProvedorCatalogo {
  provedor: ProvedorComunicacao;
  capacidades: CapacidadeCanal[];
  execucaoDisponivel: boolean;
  credenciaisVerificadas: boolean;
}

/** GET /catalogo/formatacoes */
export interface FormatacaoCatalogo {
  id: FormatoVariavel;
  tipos: TipoPropriedade[];
  exemplo: string;
}

/** GET /disparos/catalogo/modelos-meta/:nome — o modelo como está aprovado na Meta. */
export interface ModeloMeta {
  id: string | null;
  nome: string;
  idioma: string;
  status: string | null;
  categoria: string | null;
  formatoVariaveis: "POSICAO" | "NOME";
  cabecalho: {
    tipo: "TEXTO" | "IMAGEM" | "VIDEO" | "DOCUMENTO" | "OUTRO";
    texto: string | null;
    variavel: string | null;
    exigeParametro: boolean;
    /** Cópia no nosso storage da mídia aprovada na Meta; null se a cópia falhou ou o cabeçalho não é mídia. */
    linkMidia: string | null;
  } | null;
  corpo: { texto: string; variaveis: string[] };
  botoes: { indice: number; tipo: string; texto: string; url: string | null; exigeParametro: boolean }[];
}

export interface FrequenciaCatalogo {
  id: FrequenciaDisparo;
  campos: string[];
  intervalos?: number[];
  diasSemana?: number[];
  diaMesMinimo?: number;
  diaMesMaximo?: number;
}

/** GET /catalogo/agendamentos */
export interface AgendamentoCatalogo {
  timezone: string;
  frequencias: FrequenciaCatalogo[];
  observacao: string;
}

/**
 * Resposta do GET /catalogo/entidades. Desde o suporte a filtros em árvore
 * ela é um objeto — antes era o array de entidades direto.
 */
export interface RespostaEntidadesCatalogo {
  combinadores: CombinadorFiltro[];
  entidades: EntidadeCatalogo[];
}

/** Os seis catálogos juntos — o que a tela precisa pra montar o formulário. */
export interface CatalogoDisparo {
  eventos: EventoCatalogo[];
  entidades: EntidadeCatalogo[];
  /** Combinadores que o backend aceita num grupo de filtros. */
  combinadores: CombinadorFiltro[];
  acoes: AcaoCatalogo[];
  canais: ProvedorCatalogo[];
  formatacoes: FormatacaoCatalogo[];
  agendamentos: AgendamentoCatalogo;
}

// ---------------------------------------------------------------------------
// Configuração de disparo — a "receita" gravada em disparo_configuracao
// ---------------------------------------------------------------------------

export interface AgendamentoDisparo {
  frequencia: FrequenciaDisparo;
  timezone: string;
  /** MINUTOS/HORAS */
  intervalo?: number;
  /** DIARIO/SEMANAL/MENSAL — "HH:mm" */
  hora?: string;
  /** SEMANAL — domingo = 0 */
  diaSemana?: number;
  /** MENSAL — 1 a 28, pra ocorrer em todos os meses */
  diaMes?: number;
}

export interface GatilhoDisparo {
  tipo: TipoGatilho;
  /** Só no gatilho EVENTO. */
  evento?: string;
  /** Só no gatilho ROTINA. */
  agendamento?: AgendamentoDisparo;
}

/**
 * Condição de filtro — a folha da árvore.
 *
 * `valores` tem 1 item (IGUAL, MAIOR_OU_IGUAL, MENOR_QUE), 2 (ENTRE, com
 * início anterior ao fim, que é exclusivo) ou 1..30 (EM).
 */
export interface FiltroDisparo {
  propriedade: string;
  operador: OperadorFiltro;
  valores: string[];
}

/** Condição dentro da árvore: a mesma condição, marcada como nó. */
export interface FiltroCondicaoDisparo extends FiltroDisparo {
  tipoNo: TipoNoFiltro.CONDICAO;
}

/**
 * Grupo de filtros — o parêntese. Um combinador (E ou OU) e os nós que ele
 * combina; um grupo pode conter outro grupo até o teto de profundidade.
 */
export interface FiltroGrupoDisparo {
  tipoNo: TipoNoFiltro.GRUPO;
  combinador: CombinadorFiltro;
  itens: FiltroNoDisparo[];
}

export type FiltroNoDisparo = FiltroGrupoDisparo | FiltroCondicaoDisparo;

/**
 * Uma variável do template. O texto usa `{{apelido}}` e este mapa diz de
 * onde o apelido é preenchido: uma `propriedade` do contexto OU uma
 * `constante` (exclusivas entre si). Variável opcional exige `fallback`.
 */
export interface VariavelTemplate {
  propriedade?: string;
  constante?: string;
  formato: FormatoVariavel;
  obrigatoria: boolean;
  fallback?: string;
}

export interface ParametroExterno {
  componente: ComponenteExterno;
  /** Zero-based, contínuo por componente (sem lacuna nem repetição). */
  indice: number;
  valor: string;
}

export interface ReferenciaExterna {
  identificador: string;
  /** Meta exige; Vonex não recebe. */
  idioma?: string;
  /** Vonex exige (numérico); Meta não recebe. */
  produtoId?: string;
  parametros: ParametroExterno[];
}

export interface TemplateDisparo {
  modo: ModoTemplate;
  /** CORPO_PROPRIO */
  conteudo?: string;
  /** CORPO_PROPRIO, exclusivo de e-mail (que o exige). */
  assunto?: string;
  /** CORPO_PROPRIO. HTML é exclusivo de e-mail. */
  tipoConteudo?: TipoConteudo;
  variaveis: Record<string, VariavelTemplate>;
  /** REFERENCIA_EXTERNA */
  referenciaExterna?: ReferenciaExterna;
}

export interface OrigemContato {
  /** Propriedade do contexto com o contato — tipo EMAIL ou TELEFONE, conforme o canal. */
  propriedade: string;
  fallbacks?: string[];
}

export interface CanalDisparo {
  acao: AcaoDisparo;
  canal: CanalComunicacao;
  provedor: ProvedorComunicacao;
  remetente: string;
  origemContato: OrigemContato;
  /** Template reutilizável. Vínculo vivo: ao salvar, o backend troca `template` pelo conteúdo dele. */
  templateId?: string;
  /** Template embutido. Obrigatório quando não há templateId. */
  template?: TemplateDisparo;
}

/** GET /disparos/:id — a configuração completa. */
export interface DisparoConfiguracao {
  id: string;
  nome: string;
  ativo: boolean;
  versao: number;
  contexto: ContextoDisparo;
  destinatario: DestinatarioDisparo;
  gatilho: GatilhoDisparo;
  expressaoCron: string | null;
  /**
   * Árvore gravada. Cadastro anterior à árvore ficou como lista plana no
   * banco e volta assim — quem lê normaliza (`arvoreDeFiltrosApi`) em vez de
   * supor um formato só.
   */
  filtros: FiltroGrupoDisparo | FiltroDisparo[];
  canais: CanalDisparo[];
  criadoPor: string | null;
  atualizadoPor: string | null;
  criadoEm: string;
  atualizadoEm: string;
}

/** GET /disparos — visão enxuta da grade (o detalhe fica no GET por id). */
export interface DisparoListItem {
  id: string;
  nome: string;
  ativo: boolean;
  versao: number;
  contexto: ContextoDisparo;
  gatilho: GatilhoDisparo;
  canais: { canal: CanalComunicacao; provedor: ProvedorComunicacao }[];
}

export interface PaginaDisparos {
  items: DisparoListItem[];
  total: number;
  pagina: number;
  limite: number;
}

/**
 * Resposta do GET /disparos/resumo — a faixa de tags acima da tabela.
 *
 * Existe como chamada própria porque a grade é paginada e `GET /disparos`
 * devolve só `total`: os outros quatro números não saem da página carregada.
 * Aceita o mesmo `contexto` da listagem, para a faixa contar o mesmo conjunto
 * que a paginação numera no rodapé.
 */
export interface DisparosTotais {
  total: number;
  ativos: number;
  eventos: number;
  rotinas: number;
  manuais: number;
}

// ---------------------------------------------------------------------------
// DTOs de entrada
// ---------------------------------------------------------------------------

export interface CriarDisparoDto {
  nome: string;
  /** Default false: nasce rascunho. */
  ativo?: boolean;
  contexto: ContextoDisparo;
  destinatario: DestinatarioDisparo;
  gatilho: GatilhoDisparo;
  /**
   * Árvore GRUPO(E|OU)/CONDICAO. Até 20 condições na árvore inteira e
   * profundidade 3 (a condição conta como nível). ROTINA e MANUAL exigem ao
   * menos uma condição.
   */
  filtros: FiltroGrupoDisparo;
  /** 1 a 3, um por canal. */
  canais: CanalDisparo[];
}

/** PUT /disparos/:id — corpo do POST mais a versão (lock otimista). */
export interface AtualizarDisparoDto extends CriarDisparoDto {
  versao: number;
}

/** PATCH /disparos/:id/ativo — ativar revalida o cadastro inteiro. */
export interface AtivarDisparoDto {
  ativo: boolean;
  versao: number;
}

/** DELETE /disparos/:id — versão vai no corpo. */
export interface VersaoDisparoDto {
  versao: number;
}

export interface ListarDisparosParams {
  pagina?: number;
  /** Máx. 100. */
  limite?: number;
  ativo?: boolean;
  contexto?: ContextoDisparo;
  /** Recorta pelo tipo de gatilho — é o que a faixa de tags conta. */
  tipoGatilho?: TipoGatilho;
}

/** POST /disparos/preview — prévia da busca do cron, sem enviar nada. */
export interface PreviewBuscaDto {
  contexto: ContextoDisparo;
  filtros: FiltroGrupoDisparo;
  /** Máx. 20 (default 5). */
  limite?: number;
  propriedades?: string[];
}

export interface PreviewResultado {
  contexto: ContextoDisparo;
  /** O backend devolve a árvore já normalizada. */
  filtrosAplicados: FiltroNoDisparo;
  propriedades: string[];
  limite: number;
  quantidadeRetornada: number;
  amostra: Record<string, unknown>[];
  /** Sempre false na 007 — prévia nunca envia. */
  execucao: boolean;
}

/**
 * POST /disparos/:id/executar-agora com `teste`: a mensagem de cada canal vai
 * só para o destino informado, com valores de exemplo nas variáveis. Não
 * consulta o público, não exige o disparo ativo nem com filtro e não grava
 * notificação.
 */
export interface TestarDisparoDto {
  teste: {
    /**
     * Um por canal configurado: e-mail, ou 55 + DDD + número (só dígitos) para
     * SMS e WhatsApp — a Meta envia o número como recebe, sem completar o país.
     */
    destinos: Partial<Record<CanalComunicacao, string>>;
  };
}

export interface ResultadoTesteCanal {
  canal: CanalComunicacao;
  provedor: ProvedorComunicacao;
  /** Ausente quando o canal ficou sem destino informado. */
  destino?: string;
  ok: boolean;
  provedorMensagemId?: string | null;
  /** Por que não enviou: destino ausente ou recusa do provedor. */
  motivo?: string;
}

export interface ResultadoTesteDisparo {
  /** true quando ao menos um canal enviou. */
  ok: boolean;
  /** Prova de que o backend rodou em modo teste, e não a execução manual para o público. */
  teste: true;
  disparoId: string;
  contexto: ContextoDisparo;
  enviados: number;
  falhas: number;
  canais: ResultadoTesteCanal[];
  observacao?: string;
}

// ---------------------------------------------------------------------------
// Templates reutilizáveis — /disparos/templates
// ---------------------------------------------------------------------------

export interface DisparoTemplateItem {
  id: string;
  nome: string;
  ativo: boolean;
  contexto: ContextoDisparo;
  canal: CanalComunicacao;
  provedor: ProvedorComunicacao;
  modo: ModoTemplate;
  conteudo: TemplateDisparo;
  criadoEm: string;
  atualizadoEm: string;
}

export interface CriarDisparoTemplateDto {
  nome: string;
  ativo?: boolean;
  contexto: ContextoDisparo;
  canal: CanalComunicacao;
  provedor: ProvedorComunicacao;
  template: TemplateDisparo;
}

export interface ListarDisparoTemplatesParams {
  ativo?: boolean;
  contexto?: ContextoDisparo;
  canal?: CanalComunicacao;
  provedor?: ProvedorComunicacao;
}

// ---------------------------------------------------------------------------
// Rótulos
// ---------------------------------------------------------------------------

export const CANAL_LABEL: Record<CanalComunicacao, string> = {
  [CanalComunicacao.EMAIL]: "E-mail",
  [CanalComunicacao.SMS]: "SMS",
  [CanalComunicacao.WHATSAPP]: "WhatsApp",
};

export const PROVEDOR_LABEL: Record<ProvedorComunicacao, string> = {
  [ProvedorComunicacao.SENDGRID]: "SendGrid",
  [ProvedorComunicacao.ZENVIA]: "Zenvia",
  [ProvedorComunicacao.META]: "Meta",
  [ProvedorComunicacao.VONEX]: "Vonex",
};

export const CONTEXTO_LABEL: Record<ContextoDisparo, string> = {
  [ContextoDisparo.PROPOSTA]: "Proposta",
  [ContextoDisparo.PROPOSTA_LEILAO]: "Proposta de leilão",
  [ContextoDisparo.COBRANCA]: "Cobrança CLT",
};

// colorScheme do badge de contexto na grade de disparos.
export const CONTEXTO_COLORS: Record<ContextoDisparo, string> = {
  [ContextoDisparo.PROPOSTA]: "blue",
  [ContextoDisparo.PROPOSTA_LEILAO]: "purple",
  [ContextoDisparo.COBRANCA]: "orange",
};

// O enum e os mapas acima nomeiam só os três contextos da 007. Os demais
// (competência, desligamento, boleto…) chegam pelo catálogo do backend, que é a
// fonte deles — sem o catálogo, o contexto aparece pelo próprio id.
type ComEntidades = Pick<CatalogoDisparo, "entidades">;

export const rotuloContexto = (contexto: string, catalogo?: ComEntidades): string =>
  CONTEXTO_LABEL[contexto as ContextoDisparo] ??
  catalogo?.entidades.find((e) => e.id === contexto)?.descricao ??
  contexto;

export const corContexto = (contexto: string): string =>
  CONTEXTO_COLORS[contexto as ContextoDisparo] ?? "gray";

/** Contextos para os selects de filtro: os do catálogo, ou os três do enum enquanto ele não chega. */
export const contextosDisponiveis = (catalogo?: ComEntidades): ContextoDisparo[] =>
  catalogo?.entidades.length ? catalogo.entidades.map((e) => e.id) : Object.values(ContextoDisparo);

export const TIPO_GATILHO_LABEL: Record<TipoGatilho, string> = {
  [TipoGatilho.EVENTO]: "Evento",
  [TipoGatilho.ROTINA]: "Rotina (agendada)",
  [TipoGatilho.MANUAL]: "Manual",
};

export const DESTINATARIO_LABEL: Record<DestinatarioDisparo, string> = {
  [DestinatarioDisparo.TOMADOR]: "Tomador",
  [DestinatarioDisparo.EMPREGADOR]: "Empregador",
};

export const MODO_TEMPLATE_LABEL: Record<ModoTemplate, string> = {
  [ModoTemplate.CORPO_PROPRIO]: "Escrever a mensagem",
  [ModoTemplate.REFERENCIA_EXTERNA]: "Template aprovado no provedor",
};

export const OPERADOR_LABEL: Record<OperadorFiltro, string> = {
  [OperadorFiltro.IGUAL]: "É igual a",
  [OperadorFiltro.EM]: "É um dos valores",
  [OperadorFiltro.MAIOR_OU_IGUAL]: "A partir de",
  [OperadorFiltro.MENOR_QUE]: "Antes de",
  [OperadorFiltro.ENTRE]: "No período",
};

const OPERADOR_LABEL_NUMERO: Partial<Record<OperadorFiltro, string>> = {
  [OperadorFiltro.MAIOR_OU_IGUAL]: "Maior ou igual a",
  [OperadorFiltro.MENOR_QUE]: "Menor que",
  [OperadorFiltro.ENTRE]: "Entre",
};

export const FREQUENCIA_LABEL: Record<FrequenciaDisparo, string> = {
  [FrequenciaDisparo.MINUTOS]: "A cada N minutos",
  [FrequenciaDisparo.HORAS]: "A cada N horas",
  [FrequenciaDisparo.DIARIO]: "Diário",
  [FrequenciaDisparo.SEMANAL]: "Semanal",
  [FrequenciaDisparo.MENSAL]: "Mensal",
};

export const FORMATO_LABEL: Record<FormatoVariavel, string> = {
  [FormatoVariavel.TEXTO]: "Texto",
  [FormatoVariavel.NUMERO]: "Número",
  [FormatoVariavel.DECIMAL_BR]: "Decimal (pt-BR)",
  [FormatoVariavel.MOEDA_BRL]: "Moeda (R$)",
  [FormatoVariavel.DATA_BR]: "Data (dd/mm/aaaa)",
  [FormatoVariavel.COMPETENCIA]: "Competência (mm/aaaa)",
  [FormatoVariavel.CPF]: "CPF",
  [FormatoVariavel.CPF_MASCARADO]: "CPF mascarado",
  [FormatoVariavel.CNPJ]: "CNPJ",
};

export const COMPONENTE_EXTERNO_LABEL: Record<ComponenteExterno, string> = {
  CORPO: "Corpo",
  BOTAO_URL: "Botão (URL)",
  CABECALHO_IMAGEM: "Cabeçalho (imagem ou texto)",
};

export const DIAS_SEMANA = [
  { valor: 0, label: "Domingo" },
  { valor: 1, label: "Segunda-feira" },
  { valor: 2, label: "Terça-feira" },
  { valor: 3, label: "Quarta-feira" },
  { valor: 4, label: "Quinta-feira" },
  { valor: 5, label: "Sexta-feira" },
  { valor: 6, label: "Sábado" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A ação é sempre derivada do canal — o backend recusa se divergirem. */
export const acaoDoCanal = (canal: CanalComunicacao): AcaoDisparo =>
  `ENVIAR_${canal}` as AcaoDisparo;

/** Quantos valores o operador espera. `null` = lista livre (1 a 30). */
export const quantidadeValores = (operador: OperadorFiltro): number | null =>
  operador === OperadorFiltro.ENTRE ? 2 : operador === OperadorFiltro.EM ? null : 1;

/** Os operadores de comparação servem a data e a número; o rótulo sai do tipo do campo. */
export const rotuloOperador = (operador: OperadorFiltro, tipo?: TipoPropriedade): string =>
  (tipo === TipoPropriedade.NUMERO && OPERADOR_LABEL_NUMERO[operador]) || OPERADOR_LABEL[operador];

export const rotulosIntervalo = (tipo?: TipoPropriedade): [string, string] =>
  tipo === TipoPropriedade.NUMERO ? ["De", "Até (exclusivo)"] : ["Início", "Fim (exclusivo)"];

/** Tipo de propriedade que serve de contato para o canal. */
export const tipoContatoDoCanal = (canal: CanalComunicacao): TipoPropriedade =>
  canal === CanalComunicacao.EMAIL ? TipoPropriedade.EMAIL : TipoPropriedade.TELEFONE;

/** Só e-mail tem assunto e aceita corpo em HTML. */
export const canalAceitaAssunto = (canal: CanalComunicacao): boolean =>
  canal === CanalComunicacao.EMAIL;

/** Nome de variável aceito pelo backend: letra seguida de letras/dígitos/underscore. */
export const CHAVE_VARIAVEL_REGEX = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

/** Extrai os apelidos usados num texto, na ordem em que aparecem. */
export const extrairVariaveis = (texto: string): string[] => {
  const encontradas: string[] = [];
  const re = /\{\{\s*([A-Za-z][A-Za-z0-9_]{0,63})\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(texto)) !== null) {
    if (!encontradas.includes(m[1])) encontradas.push(m[1]);
  }
  return encontradas;
};

/**
 * Trechos com chave de variável malformada: `{{1nome}}`, `{{código}}`, um
 * `{{nome` sem fechar. É a mesma regra do DisparoConfiguracaoValidator, que
 * recusava ao salvar com "Sintaxe de variável inválida" sem que a tela
 * tivesse apontado nada: tira os tokens válidos e o que sobrar com `{{` ou
 * `}}` é erro. Devolve os trechos para a tela mostrar qual é.
 */
export const variaveisMalformadas = (texto: string): string[] => {
  const restante = texto.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_]{0,63})\s*\}\}/g, " ");
  if (!restante.includes("{{") && !restante.includes("}}")) return [];
  // Fechado (`{{ código }}`), aberto sem fechar (`{{nome`) ou fechamento
  // solto (`nome}}`). `<>` fica de fora para não arrastar tag do HTML do e-mail.
  const trechos =
    restante.match(/\{\{[^{}<>]{0,40}\}\}|\{\{[^{}\s<>]{0,20}|[^{}\s<>]{0,20}\}\}/g) ?? [];
  return [...new Set(trechos)];
};

/** Placeholder herdado das réguas (`[NOME DO CLIENTE]`) — o backend recusa salvar. */
export const PLACEHOLDER_COLCHETES_REGEX = /\[[A-ZÀ-Ú][A-ZÀ-Ú0-9_ ]*\]/;

/** Token inserido no texto a partir do apelido. */
export const tokenVariavel = (apelido: string): string => `{{${apelido}}}`;

/**
 * Apelido sugerido para uma propriedade: `tomador.nome` → `tomadorNome`.
 * O backend não aceita ponto na chave, então o caminho vira camelCase.
 */
export const apelidoDaPropriedade = (propriedade: string): string => {
  const partes = propriedade.split(".").filter(Boolean);
  const [primeira, ...resto] = partes;
  const bruto = [primeira, ...resto.map((p) => p.charAt(0).toUpperCase() + p.slice(1))].join("");
  const limpo = bruto.replace(/[^A-Za-z0-9_]/g, "");
  return CHAVE_VARIAVEL_REGEX.test(limpo) ? limpo : `var${limpo}`;
};

/** Garante um apelido livre, somando sufixo numérico quando já existe. */
export const apelidoDisponivel = (base: string, usados: Iterable<string>): string => {
  const ocupados = new Set(usados);
  if (!ocupados.has(base)) return base;
  let i = 2;
  while (ocupados.has(`${base}${i}`)) i += 1;
  return `${base}${i}`;
};

/** `PROPOSTA_PAGAMENTO_PENDENTE` → `Proposta pagamento pendente`. */
export const humanizarCodigo = (codigo?: string | null): string => {
  if (!codigo) return "—";
  const texto = codigo.replace(/_/g, " ").toLowerCase();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

export const formatDateBr = (iso?: string | null): string => {
  if (!iso) return "—";
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? "—" : data.toLocaleDateString("pt-BR");
};

/** Frase curta do agendamento — o mesmo que a expressão cron diz, em português. */
export const descreverAgendamento = (agendamento?: AgendamentoDisparo): string => {
  if (!agendamento) return "—";
  const { frequencia, intervalo, hora, diaSemana, diaMes } = agendamento;
  switch (frequencia) {
    case FrequenciaDisparo.MINUTOS:
      return `A cada ${intervalo ?? "?"} min`;
    case FrequenciaDisparo.HORAS:
      return `A cada ${intervalo ?? "?"}h`;
    case FrequenciaDisparo.DIARIO:
      return `Diário às ${hora ?? "?"}`;
    case FrequenciaDisparo.SEMANAL: {
      const dia = DIAS_SEMANA.find((d) => d.valor === diaSemana)?.label ?? "?";
      return `${dia} às ${hora ?? "?"}`;
    }
    case FrequenciaDisparo.MENSAL:
      return `Dia ${diaMes ?? "?"} às ${hora ?? "?"}`;
    default:
      return "—";
  }
};

/** Resumo do gatilho pra grade e para o cabeçalho do detalhe. */
export const descreverGatilho = (gatilho: GatilhoDisparo): string => {
  switch (gatilho.tipo) {
    case TipoGatilho.EVENTO:
      return humanizarCodigo(gatilho.evento);
    case TipoGatilho.ROTINA:
      return descreverAgendamento(gatilho.agendamento);
    case TipoGatilho.MANUAL:
      return "Sob demanda";
    default:
      return "—";
  }
};

/** Valores que um filtro precisa pra estar completo, dado o operador. */
export const filtroEstaCompleto = (filtro: FiltroDisparo): boolean => {
  const preenchidos = filtro.valores.filter((v) => v.trim() !== "");
  const esperado = quantidadeValores(filtro.operador);
  return esperado === null ? preenchidos.length >= 1 : preenchidos.length === esperado;
};
